import React from "react";
import { Controller } from "react-hook-form";
import { Button, Form, Label } from "semantic-ui-react";

import { useWindowWidth } from "@common/components/helpers/useWindowWidth";
import useLogin from "@common/components/hooks/useLogin";

/**
 * Toggle component for selecting if something is Yes or No.
 *
 * @typedef {object} ToggleProps
 * @property {function} control - Control function from react-hook-form.
 * @property {string} name - The name of the field.
 * @property {Function} [handleTrue = () => {}] - A function to run on true toggle.
 * @property {Function} [handleFalse = () => {}] - A function to run on false toggle.
 * @property {string} [label=''] - The label text to display.
 * @property {Object} [error=null] - Object containing validation error.
 * @property {string} [fieldStyle='flex-between flex-row-center my-1'] - The style of the field.
 * @property {string} [labelStyle='p-0'] - The style of the label.
 * @property {string} [buttonsContainerStyle=''] - The style of the buttons container.
 *
 * @param {ToggleProps} props
 * @returns {JSX.Element} The rendered toggle component.
 */
export default function ToggleComponent({
  control,
  name,
  handleTrue = () => {},
  handleFalse = () => {},
  label = "",
  error = null,
  fieldStyle = "flex-between flex-row-center my-1",
  labelStyle = "p-0",
  buttonsContainerStyle = "",
}) {
  const width = useWindowWidth();

  const [{ isInReadMode }] = useLogin();

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={null}
      render={({ field: { onChange, value } }) => (
        <>
          <Form.Field className={fieldStyle}>
            <label className={`${width > 700 ? "w-60" : "w-40"} ${labelStyle}`}>
              {label}
            </label>
            <div className={buttonsContainerStyle}>
              <Button
                onClick={() => {
                  onChange(true);
                  handleTrue();
                }}
                type="button"
                positive={value === true}
                disabled={isInReadMode}
              >
                Yes
              </Button>
              <Button
                onClick={() => {
                  onChange(false);
                  handleFalse();
                }}
                type="button"
                positive={value === false}
                disabled={isInReadMode}
              >
                No
              </Button>
            </div>
          </Form.Field>
          <Form.Field>
            {error && <Label prompt>{error?.message}</Label>}
          </Form.Field>
        </>
      )}
    />
  );
}
